import {
    isExerciseItem,
    newHistory,
    PersonalScheduleProgram,
    PersonalScheduleProgramHistory,
    PersonalScheduleList,
    Program
} from '@/domain'
import {
    isExerciseReference,
    referenceBelongsToScheduleDay,
    hasHistoryForScheduleDay,
    replaceScheduleDayInReference,
    resolveExerciseReference
} from '@/lib/scheduleReference'

function stableValueKey(value: unknown): string {
    if (Array.isArray(value)) {
        return `[${value.map(stableValueKey).join(',')}]`
    }
    if (value && typeof value === 'object') {
        const record = value as Record<string, unknown>
        return `{${Object.keys(record)
            .sort()
            .map(
                (key) => `${JSON.stringify(key)}:${stableValueKey(record[key])}`
            )
            .join(',')}}`
    }
    return JSON.stringify(value) ?? String(value)
}

function composeSplitScheduleId(
    dayId: string,
    lessonId: string,
    sourceScheduleId: string | undefined,
    schedules: PersonalScheduleList[]
): string {
    const base = `v2:${dayId.length}:${dayId}${lessonId.length}:${lessonId}`
    for (let suffix = 0; ; suffix += 1) {
        const candidate = suffix === 0 ? base : `${base}:${suffix}`
        const occupant = schedules.find((entry) => entry.id === candidate)
        if (
            !occupant ||
            (occupant.lessonId === lessonId &&
                occupant.sourceScheduleId === sourceScheduleId)
        ) {
            return candidate
        }
    }
}

/** Ensure history array exists (legacy/corrupt pps payloads). */
export function ensurePpsHistory(
    pps: PersonalScheduleProgram
): PersonalScheduleProgram {
    return {
        ...pps,
        history: Array.isArray(pps.history) ? pps.history : []
    }
}

export function upsertHistoryEntry(
    pps: PersonalScheduleProgram,
    entry: PersonalScheduleProgramHistory
): PersonalScheduleProgram {
    const base = ensurePpsHistory(pps)
    const prior = base.history.filter(
        (h) => !(h.kind === entry.kind && h.referenceId === entry.referenceId)
    )
    return {
        ...base,
        history: [entry, ...prior]
    }
}

function countableExercises(programme: Program, dayLessonId: string) {
    const lesson = (programme.lessons || []).find((l) => l.id === dayLessonId)
    if (!lesson) return null
    return (lesson.items || []).filter(
        (item) => isExerciseItem(item) && item.kind !== 'timeout'
    )
}

/** Enrollment "age" for cross-id winner: latest schedule.started, else timestampUTC. */
export function enrollmentStartedAt(pps: PersonalScheduleProgram): number {
    const base = ensurePpsHistory(pps)
    let latest = 0
    for (const h of base.history) {
        if (
            h.kind === 'programme.schedule.started' &&
            (h.startedAt || 0) > latest
        ) {
            latest = h.startedAt || 0
        }
    }
    if (latest > 0) return latest
    return pps.timestampUTC || 0
}

/** True when every non-timeout exercise on a schedule day is finished. */
export function isScheduleDayComplete(
    programme: Program,
    scheduleDayId: string,
    lessonId: string,
    history: PersonalScheduleProgramHistory[],
    extraFinishedRef?: string,
    scheduleContext?: PersonalScheduleList[]
): boolean {
    if (
        history.some(
            (entry) =>
                entry.kind === 'workout.finished' &&
                entry.referenceId === scheduleDayId
        )
    ) {
        return true
    }
    const exercises = countableExercises(programme, lessonId)
    if (!exercises || exercises.length === 0) return false

    const finishedReferences = history
        .filter((entry) => entry.kind === 'exercise.finished')
        .map((entry) => entry.referenceId)
    if (extraFinishedRef) finishedReferences.push(extraFinishedRef)

    return exercises.every((exercise) =>
        finishedReferences.some((referenceId) => {
            if (!scheduleContext) {
                return isExerciseReference(
                    referenceId,
                    scheduleDayId,
                    exercise.id
                )
            }
            const resolved = resolveExerciseReference(
                referenceId,
                programme,
                scheduleContext
            )
            return (
                resolved?.scheduleDayId === scheduleDayId &&
                resolved.exerciseId === exercise.id
            )
        })
    )
}

/**
 * True when every non-timeout exercise on every personal schedule day
 * has an `exercise.finished` history entry.
 * Requires at least one countable exercise in the programme.
 */
export function isProgrammeFullyComplete(
    programme: Program,
    pps: PersonalScheduleProgram,
    extraFinishedRef?: string
): boolean {
    const history = Array.isArray(pps.history) ? pps.history : []
    const schedule = Array.isArray(pps.schedule) ? pps.schedule : []
    if (schedule.length === 0) return false

    let countable = 0
    for (const day of schedule) {
        const exercises = countableExercises(programme, day.lessonId)
        if (!exercises) {
            if (
                isScheduleDayComplete(
                    programme,
                    day.id,
                    day.lessonId,
                    history,
                    extraFinishedRef,
                    schedule
                )
            ) {
                countable += 1
                continue
            }
            return false
        }
        countable += exercises.length
        if (exercises.length === 0) continue
        if (
            !isScheduleDayComplete(
                programme,
                day.id,
                day.lessonId,
                history,
                extraFinishedRef,
                schedule
            )
        ) {
            return false
        }
    }

    return countable > 0
}

/**
 * Apply a history entry (exercise finish/cancel, workout start, etc.)
 * and derive day/programme completion side effects.
 */
export function applyHistoryEntry(
    pps: PersonalScheduleProgram,
    programme: Program,
    entry: PersonalScheduleProgramHistory
): PersonalScheduleProgram {
    let next = upsertHistoryEntry(pps, entry)

    if (entry.kind === 'exercise.finished') {
        const schedule = Array.isArray(next.schedule) ? next.schedule : []
        for (const day of schedule) {
            if (
                isScheduleDayComplete(
                    programme,
                    day.id,
                    day.lessonId,
                    next.history,
                    entry.referenceId,
                    schedule
                )
            ) {
                next = upsertHistoryEntry(
                    next,
                    newHistory('workout.finished', day.id)
                )
            }
        }

        if (isProgrammeFullyComplete(programme, next, entry.referenceId)) {
            next = { ...next, status: 'finished' }
        }
    }

    return next
}

function unionHistory(
    a: PersonalScheduleProgramHistory[],
    b: PersonalScheduleProgramHistory[]
): PersonalScheduleProgramHistory[] {
    const byKey = new Map<string, PersonalScheduleProgramHistory>()
    for (const entry of [...a, ...b]) {
        const key = `${entry.kind}:${entry.referenceId}`
        const prev = byKey.get(key)
        const entryStartedAt = entry.startedAt || 0
        const prevStartedAt = prev?.startedAt || 0
        if (
            !prev ||
            entryStartedAt > prevStartedAt ||
            (entryStartedAt === prevStartedAt &&
                stableValueKey(entry) > stableValueKey(prev))
        ) {
            byKey.set(key, entry)
        }
    }
    return Array.from(byKey.values()).sort((x, y) => {
        const startedAtDifference = (y.startedAt || 0) - (x.startedAt || 0)
        if (startedAtDifference !== 0) return startedAtDifference
        return stableValueKey(y).localeCompare(stableValueKey(x))
    })
}

function resolveSameEnrollmentStatus(
    base: PersonalScheduleProgram,
    next: PersonalScheduleProgram
): PersonalScheduleProgram['status'] {
    // Archived is terminal for the same enrollment.
    if (base.status === 'archived' || next.status === 'archived') {
        return 'archived'
    }
    // Stopped is sticky for the same enrollment id (stop reuses id).
    // A stale tab cannot un-stop via progress save.
    if (base.status === 'stopped' || next.status === 'stopped') {
        return 'stopped'
    }
    // Finished sticky for same enrollment (restart uses a new id).
    if (base.status === 'finished' || next.status === 'finished') {
        return 'finished'
    }
    if (base.status === 'started' || next.status === 'started') {
        return 'started'
    }
    return stableValueKey(next.status) > stableValueKey(base.status)
        ? next.status
        : base.status
}

/**
 * Merge two personal schedule snapshots.
 *
 * - Different `pps.id` (enrollments): never field-merge — keep the newer
 *   enrollment by schedule.started / timestampUTC.
 * - Same id: union history; stopped/finished sticky; prefer non-empty schedule.
 */
export function mergePersonalSchedulePrograms(
    existing: PersonalScheduleProgram | null | undefined,
    incoming: PersonalScheduleProgram
): PersonalScheduleProgram {
    if (!existing || !existing.id) {
        return ensurePpsHistory(incoming)
    }
    if (!incoming || !incoming.id) {
        return ensurePpsHistory(existing)
    }

    const base = ensurePpsHistory(existing)
    const next = ensurePpsHistory(incoming)

    // Different enrollments: pick one whole document
    if (base.id !== next.id) {
        const baseSequence = base.enrollmentSequence ?? 0
        const nextSequence = next.enrollmentSequence ?? 0
        if (nextSequence !== baseSequence) {
            return nextSequence > baseSequence ? next : base
        }
        const baseAge = enrollmentStartedAt(base)
        const nextAge = enrollmentStartedAt(next)
        if (nextAge !== baseAge) {
            return nextAge > baseAge ? next : base
        }
        // Tie-break: prefer started > finished/stopped, then select one whole
        // document deterministically so pagination order cannot change state.
        if (next.status === 'started' && base.status !== 'started') return next
        if (base.status === 'started' && next.status !== 'started') return base
        return stableValueKey(next) > stableValueKey(base) ? next : base
    }

    // Same enrollment
    let history = unionHistory(base.history, next.history)
    const status = resolveSameEnrollmentStatus(base, next)
    const baseVersion = base.sourceContentVersion || 0
    const nextVersion = next.sourceContentVersion || 0
    const baseScheduleRevision = base.scheduleRevision ?? baseVersion
    const nextScheduleRevision = next.scheduleRevision ?? nextVersion
    const baseStartedAt = enrollmentStartedAt(base)
    const nextStartedAt = enrollmentStartedAt(next)
    const startWinner =
        nextStartedAt !== baseStartedAt
            ? nextStartedAt > baseStartedAt
                ? next
                : base
            : stableValueKey(next) > stableValueKey(base)
              ? next
              : base
    const documentWinner =
        stableValueKey(next) > stableValueKey(base) ? next : base
    // Same deterministic enrollment id can be proposed concurrently with
    // different start forms. Their schedule-day ids share a namespace but
    // describe different calendars, so never field-merge those variants.
    if (nextStartedAt !== baseStartedAt) return startWinner
    const hasSplitSourceVariants = (programme: PersonalScheduleProgram) => {
        const sourceIds = new Set<string>()
        for (const entry of Array.isArray(programme.schedule)
            ? programme.schedule
            : []) {
            if (!entry.sourceScheduleId) continue
            if (sourceIds.has(entry.sourceScheduleId)) return true
            sourceIds.add(entry.sourceScheduleId)
        }
        return false
    }
    const baseHasSplitVariants = hasSplitSourceVariants(base)
    const nextHasSplitVariants = hasSplitSourceVariants(next)
    const scheduleWinner =
        nextScheduleRevision !== baseScheduleRevision
            ? nextScheduleRevision > baseScheduleRevision
                ? next
                : base
            : nextVersion !== baseVersion
              ? nextVersion > baseVersion
                  ? next
                  : base
              : baseHasSplitVariants !== nextHasSplitVariants
                ? baseHasSplitVariants
                    ? base
                    : next
                : stableValueKey(next.schedule) > stableValueKey(base.schedule)
                  ? next
                  : base
    const alternateSchedule =
        scheduleWinner === next ? base.schedule : next.schedule
    const alternateScheduleById = new Map(
        (Array.isArray(alternateSchedule) ? alternateSchedule : []).map(
            (entry) => [entry.id, entry]
        )
    )
    const rawAlternateHistory =
        scheduleWinner === next ? base.history : next.history
    const winnerSchedule = Array.isArray(scheduleWinner.schedule)
        ? scheduleWinner.schedule
        : []
    const alternateScheduleEntries = Array.isArray(alternateSchedule)
        ? alternateSchedule
        : []
    const allScheduleDayIds = Array.from(
        new Set(
            [...winnerSchedule, ...alternateScheduleEntries].map(
                (entry) => entry.id
            )
        )
    )
    const hasUnambiguousHistoryForScheduleDay = (
        scheduleDayId: string,
        entries: PersonalScheduleProgramHistory[]
    ) =>
        entries.some((entry) =>
            referenceBelongsToScheduleDay(
                entry.referenceId,
                scheduleDayId,
                allScheduleDayIds
            )
        )
    const replaceOwnedScheduleDayReference = (
        referenceId: string,
        oldScheduleDayId: string,
        newScheduleDayId: string
    ) => {
        if (
            !referenceBelongsToScheduleDay(
                referenceId,
                oldScheduleDayId,
                allScheduleDayIds
            )
        ) {
            return undefined
        }
        return replaceScheduleDayInReference(
            referenceId,
            oldScheduleDayId,
            newScheduleDayId
        )
    }
    const alternateIdRemaps = new Map<string, string>()
    for (const alternateEntry of alternateScheduleEntries) {
        if (!alternateEntry.sourceScheduleId) continue
        const alignedWinner = winnerSchedule.find(
            (winnerEntry) =>
                winnerEntry.id !== alternateEntry.id &&
                winnerEntry.sourceScheduleId ===
                    alternateEntry.sourceScheduleId &&
                winnerEntry.lessonId === alternateEntry.lessonId
        )
        if (alignedWinner) {
            alternateIdRemaps.set(alternateEntry.id, alignedWinner.id)
        }
    }
    const alternateHistory = rawAlternateHistory.map((entry) => {
        for (const [oldId, alignedId] of alternateIdRemaps) {
            const referenceId = replaceOwnedScheduleDayReference(
                entry.referenceId,
                oldId,
                alignedId
            )
            if (referenceId) {
                return { ...entry, referenceId }
            }
        }
        return entry
    })
    history = unionHistory(alternateHistory, scheduleWinner.history)
    const splitReplacementIds = new Map<string, string>()
    for (const entry of winnerSchedule) {
        const prior = alternateScheduleById.get(entry.id)
        if (!prior || prior.lessonId === entry.lessonId) continue
        const winnerHasHistory = hasHistoryForScheduleDay(
            entry.id,
            scheduleWinner.history
        )
        const priorHasHistory = hasHistoryForScheduleDay(
            entry.id,
            alternateHistory
        )
        if (winnerHasHistory && priorHasHistory) {
            splitReplacementIds.set(
                entry.id,
                composeSplitScheduleId(
                    entry.id,
                    entry.lessonId,
                    entry.sourceScheduleId,
                    [...winnerSchedule, ...alternateScheduleEntries]
                )
            )
        }
    }
    if (splitReplacementIds.size > 0) {
        const rewrittenWinnerHistory = scheduleWinner.history.map((entry) => {
            for (const [oldId, replacementId] of splitReplacementIds) {
                const referenceId = replaceOwnedScheduleDayReference(
                    entry.referenceId,
                    oldId,
                    replacementId
                )
                if (referenceId) {
                    return { ...entry, referenceId }
                }
            }
            return entry
        })
        history = unionHistory(alternateHistory, rewrittenWinnerHistory)
    }
    const schedule = winnerSchedule
        .flatMap((entry) => {
            const prior = alternateScheduleById.get(entry.id)
            const replacementId = splitReplacementIds.get(entry.id)
            if (prior && replacementId) {
                // Both lesson variants have progress. Keep them as separate days
                // and rewrite only the winner's history to its deterministic id.
                return [{ ...entry, id: replacementId }, prior]
            }
            const scheduleWinnerHasHistory = hasHistoryForScheduleDay(
                entry.id,
                scheduleWinner.history
            )
            const alternateHasHistory = hasHistoryForScheduleDay(
                entry.id,
                scheduleWinner === next ? base.history : next.history
            )
            if (
                !prior ||
                scheduleWinnerHasHistory ||
                !alternateHasHistory ||
                prior.lessonId === entry.lessonId
            ) {
                return [entry]
            }
            // A content sync may race with progress written against the previous
            // lesson. Keep that day's lesson/date association so unioned history
            // cannot be interpreted as progress for replacement content.
            return [
                {
                    ...entry,
                    date: prior.date,
                    lessonId: prior.lessonId
                }
            ]
        })
        .filter(
            (entry, index, entries) =>
                entries.findIndex((candidate) => candidate.id === entry.id) ===
                index
        )
    for (const alternateEntry of alternateScheduleEntries) {
        const hasAlternateHistory = hasHistoryForScheduleDay(
            alternateEntry.id,
            rawAlternateHistory
        )
        const hasUnambiguousAlternateHistory =
            hasUnambiguousHistoryForScheduleDay(
                alternateEntry.id,
                rawAlternateHistory
            )
        const alreadyRepresented = schedule.some(
            (entry) =>
                entry.id === alternateEntry.id ||
                (Boolean(alternateEntry.sourceScheduleId) &&
                    entry.sourceScheduleId ===
                        alternateEntry.sourceScheduleId &&
                    entry.lessonId === alternateEntry.lessonId)
        )
        if (
            alreadyRepresented &&
            (!hasAlternateHistory || hasUnambiguousAlternateHistory)
        )
            continue
        if (hasAlternateHistory) {
            // A later sync may introduce another active lesson variant. Keep
            // every older variant that still owns history so those references
            // never become orphaned.
            schedule.push(alternateEntry)
        }
    }

    return {
        ...documentWinner,
        id: base.id,
        history,
        status,
        schedule,
        startDate: startWinner.startDate,
        starTime: startWinner.starTime,
        timestampUTC: startWinner.timestampUTC,
        endDate: schedule.reduce(
            (latest, entry) => Math.max(latest, entry.date),
            0
        ),
        sourceContentVersion: scheduleWinner.sourceContentVersion ?? 0,
        scheduleRevision:
            scheduleWinner.scheduleRevision ??
            scheduleWinner.sourceContentVersion ??
            0,
        enrollmentSequence: Math.max(
            base.enrollmentSequence ?? 0,
            next.enrollmentSequence ?? 0
        )
    }
}
